import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { interval, Subscription } from 'rxjs';
import { EcommerceService } from 'src/app/services/ecommerce.service';
import { PosTablet } from 'src/app/services/remote-control-admin.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-kiosk-build-versions',
  templateUrl: './kiosk-build-versions.component.html',
  styleUrls: ['./kiosk-build-versions.component.scss']
})
export class KioskBuildVersionsComponent implements OnInit, OnChanges, OnDestroy {

  @Input() storeId: string = '';

  tablets: PosTablet[] = [];
  allTablets: PosTablet[] = [];
  isLoading = false;
  errorMessage = '';
  endpointUnavailable = false;
  prodBuildCommit = '';

  autoRefreshEnabled = true;
  refreshIntervalSec = 30;
  countdown = 0;
  private refreshSub?: Subscription;
  private countdownSub?: Subscription;
  private loadSeq = 0;

  constructor(
    private ecommerceService: EcommerceService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.loadTablets();
    this.countdown = this.refreshIntervalSec;
    this.startAutoRefresh();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['storeId'] && !changes['storeId'].firstChange) {
      this.resetForStoreChange();
      this.loadTablets();
    }
  }

  private resetForStoreChange(): void {
    this.loadSeq++;
    this.tablets = [];
    this.allTablets = [];
    this.prodBuildCommit = '';
    this.errorMessage = '';
    this.endpointUnavailable = false;
  }

  ngOnDestroy(): void {
    this.stopAutoRefresh();
  }

  startAutoRefresh(): void {
    this.stopAutoRefresh();
    this.countdown = this.refreshIntervalSec;

    this.refreshSub = interval(this.refreshIntervalSec * 1000).subscribe(() => {
      this.loadTabletsSilent();
      this.countdown = this.refreshIntervalSec;
    });

    this.countdownSub = interval(1000).subscribe(() => {
      if (this.countdown > 0) this.countdown--;
    });
  }

  stopAutoRefresh(): void {
    this.refreshSub?.unsubscribe();
    this.countdownSub?.unsubscribe();
  }

  toggleAutoRefresh(): void {
    if (this.autoRefreshEnabled) {
      this.startAutoRefresh();
    } else {
      this.stopAutoRefresh();
    }
  }

  loadTablets(): void {
    const seq = ++this.loadSeq;
    const requestedStoreId = this.storeId;

    this.isLoading = true;
    this.errorMessage = '';
    this.endpointUnavailable = false;

    this.ecommerceService.getOnlineTablets().subscribe({
      next: (res: any) => {
        if (seq !== this.loadSeq || requestedStoreId !== this.storeId) return;
        this.applyResponse(res, true);
      },
      error: (error: any) => {
        if (seq !== this.loadSeq || requestedStoreId !== this.storeId) return;
        this.handleError(error);
      }
    });
  }

  private loadTabletsSilent(): void {
    const seq = ++this.loadSeq;
    const requestedStoreId = this.storeId;

    this.ecommerceService.getOnlineTablets().subscribe({
      next: (res: any) => {
        if (seq !== this.loadSeq || requestedStoreId !== this.storeId) return;
        this.applyResponse(res, false);
      },
      error: () => {
        if (seq !== this.loadSeq || requestedStoreId !== this.storeId) return;
      }
    });
  }

  private applyResponse(res: any, showLoading: boolean): void {
    const data = res?.response?.data ?? res?.data ?? {};
    const tablets: PosTablet[] = data.tablets || data || [];
    this.allTablets = Array.isArray(tablets) ? tablets : [];
    this.prodBuildCommit = data.prodBuildCommit || this.inferProdCommit(this.allTablets);
    this.tablets = this.storeId
      ? this.allTablets.filter(t => t.storeId === this.storeId)
      : this.allTablets;

    if (showLoading) {
      this.isLoading = false;
    }
  }

  private inferProdCommit(tablets: PosTablet[]): string {
    const nonStale = tablets.find(t => t.staleVsProd === false && t.buildCommit);
    return nonStale?.buildCommit || tablets.find(t => t.prodBuildCommit)?.prodBuildCommit || '';
  }

  private handleError(error: any): void {
    this.isLoading = false;
    if (error?.status === 404) {
      this.endpointUnavailable = true;
      this.errorMessage = 'Online tablets API extension is not deployed yet.';
      return;
    }
    this.errorMessage = error?.error?.response?.message || 'Failed to load online kiosks';
    this.toastr.error(this.errorMessage);
  }

  staleCount(): number {
    return this.tablets.filter(t => t.staleVsProd === true).length;
  }

  formatCommit(commit?: string): string {
    if (!commit) return '—';
    return commit.length > 12 ? commit.slice(0, 12) + '…' : commit;
  }

  formatTime(dateStr?: string): string {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString();
  }
}
